import { useEffect, useRef } from 'react'
import axios, { CancelToken, CancelTokenSource } from 'axios'
import { debounce } from 'lodash'

/**
 * 防抖 + CancelToken 组合 hook
 * 用于搜索输入场景：防抖减少请求频率，CancelToken 取消上一次未完成的请求，解决竞态问题
 *
 * @param delay 防抖延迟时间（ms）
 * @returns debouncedFn - 接收一个回调，回调参数为本次请求的 CancelToken
 *
 * @example
 * const debouncedSearch = useDebouncedCancel(400)
 *
 * <Input onChange={(e) => {
 *   debouncedSearch((cancelToken) => {
 *     dispatch(searchApi(params, cancelToken))
 *   })
 * }} />
 */
export function useDebouncedCancel(delay: number) {
  const sourceRef = useRef<CancelTokenSource | null>(null)

  const debounced = useRef(
    debounce((fn: (cancelToken: CancelToken) => void) => {
      sourceRef.current?.cancel()
      const source = axios.CancelToken.source()
      sourceRef.current = source
      fn(source.token)
    }, delay)
  ).current

  useEffect(() => {
    return () => {
      sourceRef.current?.cancel()
      debounced.cancel()
    }
  }, [debounced])

  return debounced
}
